import { useState, useRef, useEffect, useLayoutEffect, useCallback, isValidElement, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/** Portal-rendered tooltip with the project's standard look (matches
 *  BreadcrumbSegment). 500ms hover delay, fixed positioning, and
 *  pointer-events-none so it doesn't trap mouse events.
 *
 *  By default the tooltip only appears when the wrapped child is
 *  visually truncated (`scrollWidth > clientWidth`) — the common case
 *  for "show the full label when it overflows". Pass `always` to fire
 *  on every hover regardless of overflow (when the tooltip carries
 *  extra info that isn't visible in the trigger).
 *
 *  Positioning (every mode measured and clamped to the viewport by
 *  `placeBubble` / `placeAtCursor`, #1426 I1):
 *    - default: centred below the trigger, with an arrow pointing at it
 *    - `placement='top'`: centred above the trigger
 *    - either side flips when it has no room and the other does
 *    - `followCursor`: ~16px below the cursor and tracks mouse-move
 *      while visible (placement is ignored in this mode)
 *
 *  `tone` colours the bubble (`warning` / `error`).
 *
 *  Scroll-aware: while shown, the position is recomputed on window
 *  scroll (capture phase, so scrolls inside nested containers also
 *  trigger updates) and on viewport resize. This avoids the "tooltip
 *  stays parked while the trigger scrolls away" footgun.
 *
 *  Pass `null`/empty `content` to make the wrapper inert — useful when
 *  the parent toggles tooltip visibility based on state without
 *  conditionally re-mounting the wrapper (which would disrupt drag /
 *  focus state).
 *
 *  The trigger is rendered as a `<span>` wrapper. Use `className` to
 *  control its truncation / max-width / display, since that's what
 *  determines whether the overflow check fires.
 *
 *  `tap`: on a touch screen, a TAP on the trigger toggles the tooltip
 *  (instead of the default flash on touchstart, which also fires when a
 *  scroll starts on the trigger and is gone before a long label is read).
 *  It sits below the trigger — above when there's no room — clamped to the
 *  viewport, and stays until the next tap anywhere, a scroll or a resize.
 *  Taps on controls inside the trigger (buttons, links, inputs, menus) are
 *  theirs, not a toggle. Mouse hover is unchanged.
 */
// Controls inside a tap-mode trigger: a tap on one is the control's, never a toggle.
const TAP_CONTROLS = 'button, a, input, select, textarea, [role="menu"], [role="switch"]'

/**
 * #1426 (owner, 2026-10-27) — UNIVERSAL: on a touch screen a tooltip never shows for a tap that also fires the
 * element's action, on any surface. One rule, used by every tooltip (this one, the `data-tooltip` host, and the local
 * hover helpers):
 *  - A hover starts only from a MOUSE pointer (`pointerenter` with `pointerType === 'mouse'`, as `TooltipHost` does):
 *    the `mouseenter` a browser emulates after a tap — however long the press — is never a hover. No time window.
 *  - `isActionTap(click, trigger)`: the browser's own `click` that followed a tap is the tap's ACTION when its target —
 *    or anything between it and the trigger — is a control (`ACTION_TARGETS`) or is styled `cursor: pointer` (the same
 *    signal iOS Safari uses to decide a tap is a click at all), or when the trigger itself sits inside a control (a
 *    tooltip in a breadcrumb `<button>`: the tap navigates). Chrome sends a click for a tap on ANY element, inert text
 *    included, so "a click reached the trigger" alone would hide every touch tooltip (D1 in #1426's PR).
 */
const ACTION_TARGETS = 'button, a[href], input, select, textarea, summary, label, [role="button"], [role="link"], [role="menuitem"], [role="checkbox"], [role="switch"], [role="tab"], [onclick]'
/** #1426: the ONE hover test — a hover starts and ends only with a MOUSE pointer (a touch's pointer events and the mouse
 *  events a browser emulates after a tap are never hover input). Every hover helper asks it. */
export function isMouseHover(e: { pointerType: string }): boolean {
  return e.pointerType === 'mouse'
}
/** A press held longer than this is a long press (context menu, callout), never a tap. */
const LONG_PRESS_MS = 500
/** A touch that moves further than this from where it started is a scroll, never a tap. iOS sends touchmove for the
 *  1–3 px a fingertip drifts during an ordinary tap, so any touchmove at all is not "moved" (#1426 review round 1). */
const TAP_SLOP_PX = 10
export function isActionTap(target: EventTarget | null, trigger: Element | null): boolean {
  for (let el = target instanceof Element ? target : null; el; el = el.parentElement) {
    if (isLiveAction(el)) return true
    if (typeof getComputedStyle === 'function' && getComputedStyle(el).cursor === 'pointer') return true
    if (el === trigger) break
  }
  return insideAction(trigger)
}
/** The trigger sits inside a control (a crumb `<button>`, an identity row): a tap on it fires that control. */
function insideAction(trigger: Element | null): boolean {
  for (let el = trigger?.parentElement ?? null; el; el = el.parentElement) if (isLiveAction(el)) return true
  return false
}
/** A control a tap would fire: never a disabled one (no click reaches it — its tooltip often says WHY it is disabled),
 *  and a `<label>` only when it labels a live control (a bare label around an ⓘ hint fires nothing). */
function isLiveAction(el: Element): boolean {
  if (!el.matches(ACTION_TARGETS) || el.matches(':disabled, [aria-disabled="true"]')) return false
  if (el instanceof HTMLLabelElement) { const c = el.control; return !!c && !c.matches(':disabled') }
  return true
}
/** A child that IS an action (a button, a link, anything with an `onClick` prop): its tooltip never shows on touch —
 *  React props are inspectable even where DOM listeners are not. */
function childIsAction(children: ReactNode): boolean {
  if (!isValidElement(children)) return false
  const props = children.props as { onClick?: unknown; href?: unknown; role?: unknown; disabled?: unknown }
  if (props.disabled === true) return false   // no click reaches a disabled control: its tooltip (often the reason) shows
  return children.type === 'button' || children.type === 'a' || typeof props.onClick === 'function' || props.role === 'button'
}

/** Gap between a bubble and its trigger (room for the arrow), and the margin it keeps from the viewport's edges. */
const GAP = 8
const MARGIN = 8

/** #1426 — the ONE viewport clamp, for every axis: `start` kept `MARGIN` inside `viewSize`. The margin wins last, so a
 *  bubble bigger than the viewport sits AT the margin (its top / left visible) instead of going negative. */
function clampToViewport(start: number, size: number, viewSize: number): number {
  return Math.max(MARGIN, Math.min(start, viewSize - MARGIN - size))
}

/**
 * #1426 I1 — the ONE placement of a measured bubble beside its trigger, used by every `<Tooltip>` mode and by
 * `TooltipHost`: centred on the trigger, on the preferred side, flipped only when that side has no room AND the other
 * does, `left` clamped 8px inside the viewport (a phone's edge cut a CSS-centred bubble off). `arrow` is the trigger's
 * centre measured from the bubble's left, so the arrow still points at the trigger when the clamp moved the bubble.
 */
export function placeBubble(
  anchor: { top: number; bottom: number; left: number; right: number },
  size: { w: number; h: number },
  prefer: 'top' | 'bottom',
  view: { w: number; h: number },
): { top: number; left: number; side: 'above' | 'below'; arrow: number } {
  const roomAbove = anchor.top - GAP - size.h >= MARGIN
  const roomBelow = anchor.bottom + GAP + size.h <= view.h - MARGIN
  const above = prefer === 'top' ? roomAbove || !roomBelow : !roomBelow && roomAbove
  const centerX = (anchor.left + anchor.right) / 2
  const left = clampToViewport(centerX - size.w / 2, size.w, view.w)
  const top = clampToViewport(above ? anchor.top - GAP - size.h : anchor.bottom + GAP, size.h, view.h)
  return { top, left, side: above ? 'above' : 'below', arrow: centerX - left }
}

/** #1426 I1 — a `followCursor` bubble: 16px below the cursor (above it when below has no room and above does), its
 *  left edge at the cursor, clamped inside the viewport. */
export function placeAtCursor(
  cursor: { x: number; y: number },
  size: { w: number; h: number },
  view: { w: number; h: number },
): { top: number; left: number } {
  const left = clampToViewport(cursor.x, size.w, view.w)
  const below = cursor.y + 16 + size.h <= view.h - MARGIN
  const above = !below && cursor.y - GAP - size.h >= MARGIN
  return { top: clampToViewport(above ? cursor.y - GAP - size.h : cursor.y + 16, size.h, view.h), left }
}

/** The bubble's colours and the arrow's matching border, per `tone`. Literal classes only — Tailwind 4 sees only
 *  literals, never an interpolated `bg-${x}`. */
const TONE = {
  default: { bubble: 'bg-zinc-900 text-zinc-100 border-zinc-700', up: 'border-b-zinc-900', down: 'border-t-zinc-900' },
  warning: { bubble: 'bg-amber-950 text-amber-200 border-amber-800', up: 'border-b-amber-950', down: 'border-t-amber-950' },
  error: { bubble: 'bg-red-950 text-red-200 border-red-800', up: 'border-b-red-950', down: 'border-t-red-950' },
} as const

type BubblePos = { top: number; left: number; side?: 'above' | 'below'; arrow?: number }

export function Tooltip({
  content,
  children,
  always = false,
  followCursor = false,
  placement = 'bottom',
  tap = false,
  tone = 'default',
  className,
}: {
  content: string | ReactNode
  children: ReactNode
  always?: boolean
  followCursor?: boolean
  /** Tooltip side relative to the trigger. Default `bottom` for
   *  backward-compat. Ignored when `followCursor` is set (cursor wins). */
  placement?: 'top' | 'bottom'
  /** Touch: a tap toggles the tooltip and it stays until dismissed (see above). */
  tap?: boolean
  /** The bubble's colours: `warning` / `error` for a hint that explains a problem. */
  tone?: 'default' | 'warning' | 'error'
  className?: string
}) {
  // #1426 I2 — the ONE visibility state: what showed the bubble on screen (null = hidden). A mouse hover, a touch on a
  // non-tap trigger (the flash, dismissed after a beat), or a tap in tap mode (stays until dismissed). Hover never
  // replaces a touch- or tap-shown bubble (handleEnter); the dismissal rules below depend on it.
  const [shownBy, setShownBy] = useState<null | 'hover' | 'touch' | 'tap'>(null)
  const show = shownBy !== null
  const tapped = shownBy === 'tap'
  const ref = useRef<HTMLSpanElement>(null)
  // Set only by the placement layout effect, from the bubble's measured size; null while hidden.
  const [pos, setPos] = useState<BubblePos | null>(null)
  // Bumped to re-place a shown bubble (a scroll, a resize, a cursor move).
  const [placeTick, setPlaceTick] = useState(0)
  const cursorRef = useRef<{ x: number; y: number } | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  // THIS trigger was touched (began or ended) within the last second, so a click on it is that tap's (tap mode toggles
  // only then; the touch path claims an action tap). Hover never asks it — it asks the pointer's type.
  const lastTouchRef = useRef(0)
  const fromTouch = () => Date.now() - lastTouchRef.current < 1000
  const bubbleRef = useRef<HTMLDivElement>(null)

  const hasContent = content !== null && content !== undefined && content !== ''

  // What the bubble is placed against: the trigger's rect (centred beside it, by `placeBubble`), or — followCursor
  // outside tap mode — the stored cursor / touch point (`placeAtCursor`).
  const computeAnchor = useCallback((): { rect: DOMRect } | { cursor: { x: number; y: number } } | null => {
    if (followCursor && !tapped) return cursorRef.current ? { cursor: cursorRef.current } : null
    const el = ref.current
    return el ? { rect: el.getBoundingClientRect() } : null
  }, [followCursor, tapped])

  const handleEnter = (e: React.PointerEvent) => {
    // #1426: only a mouse hovers — a touch's pointerenter (and the mouseenter it emulates) never starts the hover path
    // a touch's or a tap's bubble on screen keeps its own lifecycle (a cursor drifting onto it must not strand it)
    if (!hasContent || !isMouseHover(e) || shownBy === 'touch' || shownBy === 'tap') return
    // a button held is a drag passing over (TooltipHost's rule), never a hover
    if (e.buttons !== 0) return
    clearTimeout(timerRef.current)
    if (followCursor) cursorRef.current = { x: e.clientX, y: e.clientY }
    timerRef.current = setTimeout(() => {
      const el = ref.current
      if (!el) return
      // Only fire when the trigger is actually overflowing — unless
      // `always` is set. Avoids tooltips on short, fully-visible text.
      if (!always && el.scrollWidth <= el.clientWidth) return
      setShownBy('hover')   // placed by the layout effect below, once the bubble can be measured
    }, 500)
  }

  const handleMove = (e: React.PointerEvent) => {
    if (!followCursor || !hasContent || tapped || !isMouseHover(e)) return
    cursorRef.current = { x: e.clientX, y: e.clientY }
    if (show) setPlaceTick((t) => t + 1)
  }

  const handleLeave = (e: React.PointerEvent) => {
    // a touch ends with a pointerleave: the touch path owns its own bubble (dismissed after a beat)
    if (tapped || !isMouseHover(e)) return
    clearTimeout(timerRef.current)
    setShownBy(null)
  }

  // a mouse press hides the hover bubble and cancels its delay (it would cover what the click opens) — TooltipHost's
  // rule. Mouse only: a touch's press starts the tap path, which owns its own bubble. A touch- or tap-shown bubble keeps
  // its own lifecycle — `timerRef` is its 2.5 s dismiss then, and clearing it would strand the bubble (a touch laptop).
  const handleDown = (e: React.PointerEvent) => {
    if (!isMouseHover(e) || shownBy === 'touch' || shownBy === 'tap') return
    clearTimeout(timerRef.current)
    setShownBy(null)
  }

  const handleClick = (e: React.MouseEvent) => {
    if (!tap || !hasContent || !fromTouch()) return
    if ((e.target as Element).closest(TAP_CONTROLS)) return
    clearTimeout(timerRef.current)
    if (tapped) { setShownBy(null); return }
    setShownBy('tap')
  }

  // Touch (#1426, owner 2026-10-27): a tooltip never shows for a tap that also fires the element's action. Nothing
  // shows on touchstart: the tap is held as PENDING and the browser's own click (a few ms after the tap) decides — a
  // click that is the tap's action (`isActionTap`, seen in the capture phase on this wrapper) drops it; a tap with no
  // action shows it once the click had its chance. A child that IS an action never shows at all; a touch that moves
  // (a scroll) drops it. Only on overflow unless `always`; dismissed after a beat.
  const pendingTouchRef = useRef<{ x: number; y: number; at: number } | null>(null)
  // its own timer: the 2.5 s dismiss of a tooltip already shown lives on `timerRef`, and a second tap must not cancel it
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  useEffect(() => () => clearTimeout(pendingTimerRef.current), [])
  // a touch moved past TAP_SLOP_PX (a scroll), a cancelled one, or a long press (its own gesture — a context menu, a callout) is no tap
  const dropPending = () => { pendingTouchRef.current = null; clearTimeout(pendingTimerRef.current) }
  // hide the bubble, whatever showed it, and everything that could still show it (an action tap, a trigger made inert,
  // a context menu)
  const dismissTouch = () => { dropPending(); clearTimeout(timerRef.current); setShownBy(null) }
  const childAction = childIsAction(children)
  // what a tap on this trigger may show — asked at touchstart and again when the pending timer fires (the trigger can
  // change in between: re-rendered wider, wrapped in a control)
  const touchMayShow = (el: HTMLElement | null): el is HTMLElement =>
    !!el && !tap && hasContent && !childAction && !insideAction(el) && (always || el.scrollWidth > el.clientWidth)
  // the timer asks the LATEST render's answer (updated after each commit, never during render)
  const touchMayShowRef = useRef(touchMayShow)
  useEffect(() => { touchMayShowRef.current = touchMayShow })
  const handleTouch = (e: React.TouchEvent) => {
    lastTouchRef.current = Date.now()
    dropPending()   // a timer left from an earlier tap must not fire during this touch
    const el = ref.current
    if (!touchMayShow(el)) return
    const t = e.touches[0]
    pendingTouchRef.current = { x: t?.clientX ?? 0, y: t?.clientY ?? 0, at: Date.now() }
  }
  const handleTouchMove = (e: React.TouchEvent) => {
    const p = pendingTouchRef.current
    const t = e.touches[0]
    if (!p || !t) return
    if (Math.hypot(t.clientX - p.x, t.clientY - p.y) > TAP_SLOP_PX) dropPending()
  }
  const handleTouchEnd = () => {
    lastTouchRef.current = Date.now()   // a press held past the window: its click still comes right after it ends
    if (!pendingTouchRef.current) return
    if (Date.now() - pendingTouchRef.current.at > LONG_PRESS_MS) { dropPending(); return }
    clearTimeout(pendingTimerRef.current)
    // the click a tap produces arrives right after touchend; give it its chance to claim the tap first
    pendingTimerRef.current = setTimeout(() => {
      const p = pendingTouchRef.current
      pendingTouchRef.current = null
      if (!p || !touchMayShowRef.current(ref.current)) return
      // a followCursor bubble a tap showed sits at the touch point (placed by the layout effect below)
      if (followCursor) cursorRef.current = { x: p.x, y: p.y }
      setShownBy('touch')
      clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => setShownBy((s) => (s === 'touch' ? null : s)), 2500)
    }, 350)
  }
  const handleClickCapture = (e: React.MouseEvent) => {
    if (tap || !fromTouch() || !isActionTap(e.target, ref.current)) return
    // the tap fired an action: drop a pending tooltip AND one an earlier inert tap is still showing
    dismissTouch()
  }

  // Reposition on scroll / resize while shown. Capture phase so that
  // scrolling INSIDE a nested container (a dialog body, a panel, etc.)
  // also triggers an update — the bubbling phase doesn't see those.
  useEffect(() => {
    if (!show || followCursor) return
    const update = () => setPlaceTick((t) => t + 1)
    window.addEventListener('scroll', update, { capture: true, passive: true })
    window.addEventListener('resize', update, { passive: true })
    return () => {
      window.removeEventListener('scroll', update, { capture: true } as EventListenerOptions)
      window.removeEventListener('resize', update)
    }
  }, [show, followCursor])

  // #1426 I1 — every mode is placed HERE, from the bubble's measured size (its first paint is hidden): hover, touch and
  // tap beside the trigger (`placeBubble`; tap mode below it, as before), followCursor at the cursor (`placeAtCursor`).
  // Re-runs when the content changes while shown (a live % or selection resizes the bubble). A hidden bubble drops its
  // position, and the box is measured with `left` reset, so a `fixed` box is never measured narrowed by a stale `left`.
  useLayoutEffect(() => {
    if (!shownBy) { setPos(null); return }
    const bubble = bubbleRef.current
    const anchor = computeAnchor()
    if (!bubble || !anchor) return
    const prevLeft = bubble.style.left
    bubble.style.left = '0px'
    const size = { w: bubble.offsetWidth, h: bubble.offsetHeight }
    bubble.style.left = prevLeft
    const view = { w: window.innerWidth, h: window.innerHeight }
    const next: BubblePos = 'cursor' in anchor
      ? placeAtCursor(anchor.cursor, size, view)
      : placeBubble(anchor.rect, size, tapped ? 'bottom' : placement, view)
    setPos((p) => (p && p.top === next.top && p.left === next.left && p.side === next.side && p.arrow === next.arrow ? p : next))
    // followCursor is a dep: a caller can toggle it on a live trigger (WhoHasAccessDialog does), which changes the anchor
    // eslint-disable-next-line react-hooks/exhaustive-deps -- computeAnchor changes only with tapped (shownBy) and followCursor, both deps
  }, [shownBy, placeTick, content, placement, followCursor])

  // #1426 I2 — the ONE dismissal of a bubble a touch or a tap showed (hover has none: the mouse leaving hides it).
  //  - 'tap': the next press anywhere but the trigger's own body hides it (a press there toggles, handleClick); a press
  //    on a control inside the trigger (a ⋯ menu) hides it too — else it would cover the menu. A scroll or resize hides.
  //  - 'touch': the next press anywhere else hides it at once (it would cover what that press opens) — the touch's own
  //    pointerleave never does (it is no hover). An anchored bubble follows its trigger on scroll (the reposition effect
  //    above), so a scroll elsewhere (a streaming chat) leaves it; a cursor-placed one can't follow, so a scroll or
  //    resize hides it.
  useEffect(() => {
    if (shownBy !== 'tap' && shownBy !== 'touch') return
    const hide = () => { clearTimeout(timerRef.current); setShownBy(null) }
    const onDown = (e: PointerEvent) => {
      const inside = !!ref.current && e.target instanceof Node && ref.current.contains(e.target)
      const onControl = shownBy === 'tap' && e.target instanceof Element && !!e.target.closest(TAP_CONTROLS)
      if (inside && !onControl) return
      hide()
    }
    document.addEventListener('pointerdown', onDown, true)
    // a press inside an iframe never reaches this document; the window losing focus is how it shows (TooltipHost's rule)
    window.addEventListener('blur', hide)
    const onViewport = shownBy === 'tap' || followCursor
    if (onViewport) {
      window.addEventListener('scroll', hide, { capture: true, passive: true })
      window.addEventListener('resize', hide)
    }
    return () => {
      document.removeEventListener('pointerdown', onDown, true)
      window.removeEventListener('blur', hide)
      window.removeEventListener('scroll', hide, { capture: true } as EventListenerOptions)
      window.removeEventListener('resize', hide)
    }
  }, [shownBy, followCursor])

  // Becoming inert (content cleared) hides the tooltip in every mode and drops a pending tap and the dismiss timer, so
  // content set again later doesn't bring a bubble back with no new interaction.
  useEffect(() => {
    if (hasContent) return
    dismissTouch()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dismissTouch only touches refs and a state setter
  }, [hasContent])

  const toneClasses = TONE[tone]

  return (
    <>
      <span
        ref={ref}
        onPointerEnter={handleEnter}
        onPointerMove={followCursor ? handleMove : undefined}
        onPointerLeave={handleLeave}
        onPointerDown={handleDown}
        onTouchStart={handleTouch}
        onTouchMove={handleTouchMove}
        onTouchCancel={dropPending}
        onContextMenu={dismissTouch}   // a long press / right-click opens a menu the bubble would cover
        onTouchEnd={handleTouchEnd}
        onClickCapture={handleClickCapture}
        onClick={tap ? handleClick : undefined}
        className={className}
      >
        {children}
      </span>
      {show && hasContent && createPortal(
        // ONE bubble for hover, touch and tap (#1426 I1): hidden until the layout effect has measured and placed it
        <div
          ref={bubbleRef}
          role="tooltip"
          className={`fixed z-[9999] px-2 py-1 text-xs rounded ${toneClasses.bubble} border whitespace-pre-wrap [overflow-wrap:anywhere] max-w-[min(20rem,calc(100vw-16px))] shadow-lg pointer-events-none`}
          style={{ top: pos?.top ?? 0, left: pos?.left ?? 0, visibility: pos ? 'visible' : 'hidden' }}
        >
          {content}
          {/* Arrow toward the trigger — offset, not centred, when the clamp moved the bubble. Border trick: a 4px box
              with every border transparent but the one pointing at the trigger renders as a small triangle. */}
          {pos?.side && (
            <div
              className={`absolute -translate-x-1/2 border-4 border-transparent ${pos.side === 'above' ? `top-full ${toneClasses.down}` : `bottom-full ${toneClasses.up}`}`}
              style={{ left: pos.arrow }}
            />
          )}
        </div>,
        document.body,
      )}
    </>
  )
}

/** Hover hint for any element carrying `data-tooltip="…"`. Mount ONE
 *  <TooltipHost /> at the app root (apps/web/src/main.tsx).
 *
 *  Toolbar icon buttons use this instead of the native `title`. The native
 *  hint's timing belongs to the OS: it waits for the pointer to go fully still,
 *  stays suppressed after a click until the pointer leaves, and never shows
 *  while the window is inactive — so on a toolbar it read as "sometimes it
 *  never appears, sometimes it takes ages". <Tooltip> above fixes the timing
 *  but wraps its trigger in a <span>, which toolbar flex layouts and `>`
 *  selectors don't survive; delegating from the document needs no wrapper.
 *
 *  Same look and 500ms delay as <Tooltip>. Mouse only — a tap is not a hover.
 *  Hidden on pointer-down and kept hidden until the pointer leaves (so it never
 *  covers the menu the click opened); on a key press, until the pointer next
 *  moves; when a scroll moves its trigger — scrolling elsewhere, like a chat
 *  streaming a reply, never cancels it; and on resize and window blur. Below the trigger, flipped above when there's no room, clamped
 *  to the viewport. `data-tooltip-placement="right"` puts it beside the trigger
 *  instead — for a vertical rail, where "below" lands on the next icon — and
 *  falls back to below when that side has no room. A visual hint only:
 *  icon-only triggers still need their `aria-label`. */
export function TooltipHost() {
  const [tip, setTip] = useState<{ text: string; placement: string | null; top: number; bottom: number; left: number; right: number; centerY: number } | null>(null)
  const [pos, setPos] = useState<{ top: number; left: number; side: 'above' | 'below' | 'right'; arrow: number } | null>(null)
  const bubbleRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let current: HTMLElement | null = null
    // The trigger the user just pressed — no hint for it until the pointer leaves.
    let suppressed: HTMLElement | null = null
    let timer: ReturnType<typeof setTimeout> | undefined
    // Watches a SHOWN hint's trigger. Pointer events can't tell us the trigger
    // went away — an overflowing toolbar drops its ⋯ button the moment the
    // action set changes, and the bubble would then hang over the bar until the
    // user happened to move the mouse.
    let liveCheck: ReturnType<typeof setInterval> | undefined
    // Where the shown hint's trigger sat, to tell a scroll that moved it from one elsewhere.
    let shownAt: { top: number; left: number } | null = null
    let pointerX = -1
    let pointerY = -1

    const hide = () => {
      clearTimeout(timer)
      timer = undefined
      clearInterval(liveCheck)
      liveCheck = undefined
      shownAt = null
      setTip(null)
      setPos(null)
    }
    const schedule = (el: HTMLElement) => {
      timer = setTimeout(() => {
        timer = undefined
        // Read at show time, so a label that changed mid-hover is the one shown.
        const text = el.getAttribute('data-tooltip')
        if (!text || !el.isConnected) return
        // Content can scroll under a resting pointer: only hint what is still under it.
        const under = document.elementFromPoint(pointerX, pointerY)
        if (!under || !el.contains(under)) return
        const r = el.getBoundingClientRect()
        shownAt = { top: r.top, left: r.left }
        liveCheck = setInterval(() => {
          if (!el.isConnected || !el.getAttribute('data-tooltip')) {
            current = null
            hide()
            return
          }
          // The trigger can also MOVE under a resting pointer: a toolbar
          // reflows when an editor gains a tool (a picture finishes loading, a
          // consent badge clears) and the button slides sideways, leaving the
          // bubble pointing at blank space until the mouse happens to twitch.
          // Same test the scroll handler makes — `current` stays set, so a
          // small move re-arms the hint in place.
          const r = el.getBoundingClientRect()
          if (shownAt && (r.top !== shownAt.top || r.left !== shownAt.left)) hide()
        }, 250)
        setTip({
          text,
          placement: el.getAttribute('data-tooltip-placement'),
          top: r.top,
          bottom: r.bottom,
          left: r.left,
          right: r.right,
          centerY: r.top + r.height / 2,
        })
      }, 500)
    }
    // Over AND move: a move is what re-arms a hint that a key press or a scroll
    // dismissed while the pointer stayed on the same trigger.
    const onPointer = (e: PointerEvent) => {
      if (!isMouseHover(e)) return
      // A held button means a drag in progress. A drag crosses other triggers
      // on its way — reordering the app rail drags an icon right along it —
      // and `suppressed` only silences the one under the press, so a bubble
      // would pop up mid-drag over the thing being dragged onto.
      //
      // Read from the EVENT, never tracked in a flag of our own. A flag set on
      // pointerdown has to be cleared by a pointerup that may never arrive:
      // the window can lose focus mid-press (a native context menu, Cmd-Tab)
      // and the release then lands somewhere else entirely. That flag would
      // stay true, this line would return on every move, and the app would
      // lose every hover hint until the next complete press — silently, with
      // a reload as the only cure. `buttons` is the browser's own state and
      // cannot desynchronise.
      if (e.buttons !== 0) return
      pointerX = e.clientX
      pointerY = e.clientY
      const el = e.target instanceof Element ? e.target.closest<HTMLElement>('[data-tooltip]') : null
      if (el !== current) {
        hide()
        current = el
        if (el && el !== suppressed) schedule(el)
        return
      }
      // Same trigger (e.g. onto the icon inside it): never restart a pending or shown hint.
      if (el && el !== suppressed && timer === undefined && shownAt === null) schedule(el)
    }
    const onOut = (e: PointerEvent) => {
      if (!current) return
      if (e.relatedTarget instanceof Node && current.contains(e.relatedTarget)) return
      current = null
      suppressed = null
      hide()
    }
    const onDown = () => {
      suppressed = current
      hide()
    }
    // Scroll events arrive from everywhere — a chat streaming a reply scrolls
    // constantly — so one only matters when it moved the shown hint's trigger.
    const onScroll = () => {
      if (!shownAt || !current) return
      const r = current.getBoundingClientRect()
      if (r.top !== shownAt.top || r.left !== shownAt.left) hide()
    }
    const onBlur = () => {
      current = null
      suppressed = null
      hide()
    }

    document.addEventListener('pointerover', onPointer, true)
    document.addEventListener('pointermove', onPointer, true)
    document.addEventListener('pointerout', onOut, true)
    document.addEventListener('pointerdown', onDown, true)
    document.addEventListener('keydown', hide, true)
    window.addEventListener('scroll', onScroll, { capture: true, passive: true })
    window.addEventListener('resize', hide)
    window.addEventListener('blur', onBlur)
    return () => {
      clearTimeout(timer)
      clearInterval(liveCheck)
      document.removeEventListener('pointerover', onPointer, true)
      document.removeEventListener('pointermove', onPointer, true)
      document.removeEventListener('pointerout', onOut, true)
      document.removeEventListener('pointerdown', onDown, true)
      document.removeEventListener('keydown', hide, true)
      window.removeEventListener('scroll', onScroll, { capture: true } as EventListenerOptions)
      window.removeEventListener('resize', hide)
      window.removeEventListener('blur', onBlur)
    }
  }, [])

  // Position from the bubble's measured size (first paint is hidden), so the
  // flip and the viewport clamp use real dimensions, not a guess.
  useLayoutEffect(() => {
    const bubble = bubbleRef.current
    if (!tip || !bubble) return
    const w = bubble.offsetWidth
    const h = bubble.offsetHeight
    if (tip.placement === 'right' && tip.right + GAP + w <= window.innerWidth - MARGIN) {
      const top = clampToViewport(tip.centerY - h / 2, h, window.innerHeight)
      setPos({ top, left: tip.right + GAP, side: 'right', arrow: tip.centerY - top })
      return
    }
    // #1426 I1: below the trigger, the same measured placement every <Tooltip> bubble uses
    setPos(placeBubble(tip, { w, h }, 'bottom', { w: window.innerWidth, h: window.innerHeight }))
  }, [tip])

  if (!tip) return null
  return createPortal(
    <div
      ref={bubbleRef}
      role="tooltip"
      className="fixed z-[9999] px-2 py-1 text-xs rounded bg-zinc-900 text-zinc-100 border border-zinc-700 whitespace-pre-wrap [overflow-wrap:anywhere] max-w-[min(20rem,calc(100vw-16px))] shadow-lg pointer-events-none"
      style={{ top: pos?.top ?? 0, left: pos?.left ?? 0, visibility: pos ? 'visible' : 'hidden' }}
    >
      {tip.text}
      {/* Arrow toward the trigger — offset, not centered, when the clamp moved the bubble. */}
      {pos && (pos.side === 'right' ? (
        <div className="absolute right-full -translate-y-1/2 border-4 border-transparent border-r-zinc-900" style={{ top: pos.arrow }} />
      ) : (
        <div
          className={`absolute -translate-x-1/2 border-4 border-transparent ${pos.side === 'above' ? 'top-full border-t-zinc-900' : 'bottom-full border-b-zinc-900'}`}
          style={{ left: pos.arrow }}
        />
      ))}
    </div>,
    document.body,
  )
}
